import { Helmet } from 'react-helmet-async';
import { filter } from 'lodash';
import { useEffect, useState, useMemo, useRef } from 'react';
import {
  Card,
  Table,
  Stack,
  Chip,
  Paper,
  Avatar,
  Button,
  Popover,
  Checkbox,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
  Grid,
  Box,
  TextField,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, getFirestore, deleteDoc, doc } from 'firebase/firestore';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
import { TableSkeleton, StatCardSkeleton } from '../components/loading';
import { UserListHead, UserListToolbar } from '../sections/@dashboard/user';
import firebaseApp from '../utils/firebaseConfig';
import { formatPhoneDisplay } from '../utils/phone';
import { matchesSearch } from '../utils/search';
import { findDuplicateKeys, memberDuplicateReason } from '../utils/members';

const TABLE_HEAD = [
  { id: 'fullName', label: 'Name', alignRight: false },
  { id: 'email', label: 'Email', alignRight: false },
  { id: 'phone', label: 'Phone', alignRight: false },
  { id: 'memberId', label: 'Member ID', alignRight: false },
  { id: 'active', label: 'Status', alignRight: false },
  { id: 'expiration', label: 'Expiration', alignRight: false },
  // Fecha en que se creó la cuenta: ordenando por aquí salen primero los
  // clientes nuevos (pedido del cliente 2026-08-20)
  { id: 'createdAt', label: 'Cuenta Creada', alignRight: false },
  { id: 'request', label: '', alignRight: false },
];

const DATE_FIELDS = ['createdAt', 'expiration', 'licenseExpiration'];

function descendingComparator(a, b, orderBy) {
  // Las fechas se comparan por tiempo (los récords sin fecha van al final)
  if (DATE_FIELDS.includes(orderBy)) {
    const ad = timestampToDate(a[orderBy]);
    const bd = timestampToDate(b[orderBy]);
    const at = ad && !Number.isNaN(ad.getTime()) ? ad.getTime() : -Infinity;
    const bt = bd && !Number.isNaN(bd.getTime()) ? bd.getTime() : -Infinity;
    if (bt < at) return -1;
    if (bt > at) return 1;
    return 0;
  }
  // Orden alfabético sin importar mayúsculas/minúsculas ni acentos
  let av = a[orderBy] ?? '';
  let bv = b[orderBy] ?? '';
  if (typeof av === 'string') av = av.toLowerCase();
  if (typeof bv === 'string') bv = bv.toLowerCase();
  if (typeof av === 'string' && typeof bv === 'string') {
    return bv.localeCompare(av, 'es', { sensitivity: 'base' });
  }
  if (bv < av) return -1;
  if (bv > av) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  let working = array;
  if (query) {
    // Cada palabra del query debe aparecer en ALGÚN campo (en cualquier orden).
    // "jorge fernandez" matchea "Jorge E. Fernández Cruz", teléfono, email, etc.
    working = filter(array, (_user) => matchesSearch(query, [
      _user.fullName,
      _user.email,
      _user.phone,
      _user.memberId,
      _user.licenseNumber || _user.gunLicense,
    ]));
  }
  const stabilizedThis = working.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function timestampToDate(timestamp) {
  if (!timestamp) return null;
  if (typeof timestamp.seconds === 'number') {
    return new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
  }
  if (typeof timestamp === 'string') return new Date(timestamp);
  if (timestamp instanceof Date) return timestamp;
  return null;
}

// Status real considera AMBOS: el flag `active` Y la fecha de expiración.
// Si `active=true` pero la fecha ya pasó, se considera expirado.
function getEffectiveStatus(user) {
  const expDate = timestampToDate(user.expiration);
  const now = new Date();
  const flagActive = user.active === true;
  const dateValid = expDate ? expDate.getTime() >= now.getTime() : false;
  if (flagActive && dateValid) return 'active';
  if (flagActive && !dateValid) return 'expired_mismatch'; // active=true pero fecha vencida
  // Caso inverso (pedido de Afía 2026-08-24): la fecha está vigente pero se
  // le olvidó prender el switch de Active al renovar
  if (!flagActive && dateValid) return 'inactive_mismatch';
  return 'expired';
}

// Etiquetas del status efectivo — las comparten la tabla y el CSV para que
// nunca se desincronicen
const STATUS_LABELS = {
  active: 'Active',
  expired_mismatch: 'Expired (mismatch)',
  inactive_mismatch: 'Vigente sin activar',
  expired: 'Expired',
};

function formatDate(timestamp) {
  const date = timestampToDate(timestamp);
  if (!date || Number.isNaN(date.getTime())) return '—';
  return date.toISOString().slice(0, 10);
}

// En el CSV las fechas vacías van en blanco (el guión de la tabla se ve raro en Excel)
function formatDateForCsv(timestamp) {
  const value = formatDate(timestamp);
  return value === '—' ? '' : value;
}

const db = getFirestore(firebaseApp);

function StatCard({ title, count, icon, color, active, onClick }) {
  return (
    <Card
      onClick={onClick}
      sx={{
        p: 3,
        cursor: 'pointer',
        transition: 'all 0.2s',
        border: active ? `2px solid ${color}` : undefined,
        bgcolor: active ? alpha(color, 0.06) : undefined,
        '&:hover': {
          borderColor: color,
          bgcolor: alpha(color, 0.04),
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 700 }}>{count}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>{title}</Typography>
        </Box>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(color, 0.12),
            color,
          }}
        >
          <Iconify icon={icon} width={28} />
        </Box>
      </Stack>
    </Card>
  );
}

// Al entrar a editar un usuario y volver, la lista debe quedar DONDE ESTABA
// (misma página, búsqueda, filtro y scroll) — pedido del cliente 2026-08-19:
// "guardo un user y me devuelve a la página 1; cuando esté en la página 100
// voy a estar tan-tan-tan hasta llegar". Expira en 1 hora.
const USERS_VIEW_KEY = 'usersViewState';

function readSavedUsersView() {
  try {
    const raw = sessionStorage.getItem(USERS_VIEW_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (Date.now() - (v.at || 0) > 60 * 60 * 1000) return null;
    return v;
  } catch (e) {
    return null;
  }
}

export default function UserPage() {
  const savedView = useMemo(readSavedUsersView, []);
  const [open, setOpen] = useState(null);
  const [page, setPage] = useState(savedView?.page || 0);
  const [order, setOrder] = useState(savedView?.order || 'asc');
  const [selected, setSelected] = useState([]);
  const [groups, setGroups] = useState([]);
  const [orderBy, setOrderBy] = useState(savedView?.orderBy || 'fullName');
  const [filterName, setFilterName] = useState(savedView?.filterName || '');
  const [rowsPerPage, setRowsPerPage] = useState(savedView?.rowsPerPage || 25);
  const [showAll, setShowAll] = useState(false);
  const [users, setUsers] = useState([]);
  const [row, setRow] = useState({});
  const [statusFilter, setStatusFilter] = useState(savedView?.statusFilter ?? null);
  // Filtro por rango de fechas (pedido de Afía 2026-08-24): se escoge si el
  // rango aplica a la fecha de expiración o a la fecha de creación de cuenta
  const [dateField, setDateField] = useState(savedView?.dateField || 'expiration');
  const [dateFrom, setDateFrom] = useState(savedView?.dateFrom || '');
  const [dateTo, setDateTo] = useState(savedView?.dateTo || '');
  const [usersLoading, setUsersLoading] = useState(true);
  const navigate = useNavigate();

  // Guardar la vista justo antes de entrar al detalle/edición de un usuario
  const goToUser = (userId) => {
    try {
      sessionStorage.setItem(USERS_VIEW_KEY, JSON.stringify({
        at: Date.now(),
        page,
        order,
        orderBy,
        filterName,
        rowsPerPage,
        statusFilter,
        dateField,
        dateFrom,
        dateTo,
        scrollY: window.scrollY,
      }));
    } catch (e) { /* ignore */ }
    navigate(userId ? `/dashboard/user/information?id=${userId}` : '/dashboard/user/information');
  };

  // Restaurar el scroll cuando la lista ya cargó
  const scrollRestoredRef = useRef(false);
  useEffect(() => {
    if (savedView?.scrollY != null && !scrollRestoredRef.current && !usersLoading && users.length > 0) {
      scrollRestoredRef.current = true;
      setTimeout(() => window.scrollTo(0, savedView.scrollY), 150);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersLoading, users]);

  useEffect(() => {
    const fetchUsers = async () => {
      setUsersLoading(true);
      try {
        const usersCollection = collection(db, 'members');
        const usersSnapshot = await getDocs(usersCollection);
        const usersData = usersSnapshot.docs.map((d) => ({ ...d.data(), id: d.id }));
        console.log(`Fetched ${usersData.length} members from Firestore`);
        setUsers(usersData);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
      setUsersLoading(false);
    };

    const fetchGroups = async () => {
      try {
        const groupsRef = collection(db, 'groups');
        const querySnapshot = await getDocs(groupsRef);
        const fetchedGroups = querySnapshot.docs.map((d) => ({ ...d.data(), id: d.id }));
        setGroups(fetchedGroups);
      } catch (error) {
        console.error('Error fetching groups:', error);
      }
    };

    fetchUsers();
    fetchGroups();
  }, []);

  const now = new Date();
  const in30Days = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

  // Cuentas duplicadas (mismo email o mismo teléfono en dos récords): son las
  // que hacen que el cliente entre y le salgan los datos de otra persona.
  // Aquí solo se AVISAN — unir o borrar lo decide el staff.
  const duplicateKeys = useMemo(() => findDuplicateKeys(users), [users]);

  const stats = useMemo(() => {
    let active = 0;
    let expired = 0;
    let expiringSoon = 0;
    let mismatched = 0;
    let inactiveMismatched = 0;
    users.forEach((u) => {
      const status = getEffectiveStatus(u);
      const expDate = timestampToDate(u.expiration);
      if (status === 'active') {
        active += 1;
        if (expDate && expDate <= in30Days && expDate > now) {
          expiringSoon += 1;
        }
      } else {
        expired += 1;
        if (status === 'expired_mismatch') mismatched += 1;
        if (status === 'inactive_mismatch') inactiveMismatched += 1;
      }
    });
    return { active, expired, expiringSoon, total: users.length, mismatched, inactiveMismatched };
  }, [users]);

  const statusFilteredUsers = useMemo(() => {
    if (!statusFilter) return users;
    if (statusFilter === 'active') return users.filter((u) => getEffectiveStatus(u) === 'active');
    if (statusFilter === 'expired') return users.filter((u) => getEffectiveStatus(u) !== 'active');
    if (statusFilter === 'expiring') {
      return users.filter((u) => {
        if (getEffectiveStatus(u) !== 'active') return false;
        const expDate = timestampToDate(u.expiration);
        return expDate && expDate <= in30Days && expDate > now;
      });
    }
    if (statusFilter === 'mismatched') {
      return users.filter((u) => getEffectiveStatus(u) === 'expired_mismatch');
    }
    if (statusFilter === 'inactive_mismatch') {
      return users.filter((u) => getEffectiveStatus(u) === 'inactive_mismatch');
    }
    return users;
  }, [users, statusFilter]);

  // Rango de fechas encima del filtro de status. Los récords SIN fecha en el
  // campo escogido quedan fuera cuando hay un rango puesto.
  const dateFilteredUsers = useMemo(() => {
    if (!dateFrom && !dateTo) return statusFilteredUsers;
    const from = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
    const to = dateTo ? new Date(`${dateTo}T23:59:59.999`) : null;
    return statusFilteredUsers.filter((u) => {
      const d = timestampToDate(u[dateField]);
      if (!d || Number.isNaN(d.getTime())) return false;
      if (from && d < from) return false;
      if (to && d > to) return false;
      return true;
    });
  }, [statusFilteredUsers, dateField, dateFrom, dateTo]);

  const handleStatClick = (filterKey) => {
    setStatusFilter(statusFilter === filterKey ? null : filterKey);
    setPage(0);
  };

  const handleOpenMenu = (event, r) => {
    setOpen(event.currentTarget);
    setRow(r);
  };

  const handleCloseMenu = () => {
    setOpen(null);
  };

  const handleEditClick = () => {
    goToUser(row.id);
    handleCloseMenu();
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = dateFilteredUsers.map((n) => n.fullName);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    const v = parseInt(event.target.value, 10);
    if (v === -1) {
      setShowAll(true);
    } else {
      setShowAll(false);
      setRowsPerPage(v);
    }
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const handleDeleteClick = (r) => async () => {
    if (!r || !r.id) return;
    if (!window.confirm(`¿Eliminar a "${r.fullName || r.email || 'este usuario'}"? Esta acción no se puede deshacer.`)) return;
    try {
      await deleteDoc(doc(db, 'members', r.id));
      setUsers((prev) => prev.filter((user) => user.id !== r.id));
      handleCloseMenu();
      alert('Usuario eliminado correctamente');
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(`Error eliminando usuario: ${error.message || 'desconocido'}`);
    }
  };

  const effectiveRowsPerPage = showAll ? dateFilteredUsers.length || 1 : rowsPerPage;
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * effectiveRowsPerPage - dateFilteredUsers.length) : 0;

  const filteredUsers = applySortFilter(dateFilteredUsers, getComparator(order, orderBy), filterName);

  // El reporte baja EXACTAMENTE lo que se ve en la tabla: mismas columnas y
  // respetando el filtro de status, la búsqueda y el orden activos.
  const downloadUsersCSV = () => {
    const csvCell = (value) => `"${String(value ?? '').replace(/"/g, '""').replace(/\s*\n\s*/g, ' ')}"`;
    const csvLine = (cells) => cells.map(csvCell).join(',');
    const header = csvLine(['Name', 'Email', 'Phone', 'Member ID', 'Status', 'Expiration', 'Cuenta Creada']);
    const rows = filteredUsers.map((user) => csvLine([
      user.fullName,
      user.email,
      formatPhoneDisplay(user.phone),
      user.memberId,
      STATUS_LABELS[getEffectiveStatus(user)],
      formatDateForCsv(user.expiration),
      formatDateForCsv(user.createdAt),
    ]));
    // El BOM es para que Excel muestre bien los acentos (Fernández, Nuñez...)
    const csvString = `\ufeff${[header, ...rows].join('\r\n')}\r\n`;
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'user_report.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const isNotFound = !filteredUsers.length && !!filterName;

  const filterLabel = statusFilter === 'active' ? 'Active Members' : statusFilter === 'expired' ? 'Expired Members' : statusFilter === 'expiring' ? 'Expiring Soon' : null;

  return (
    <>
      <Helmet>
        <title>Users | BBTC Dashboard</title>
      </Helmet>

      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={4}>
          <Stack direction="row" alignItems="baseline" spacing={2}>
            <Typography variant="h4">Users</Typography>
            {!usersLoading && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {users.length} total
              </Typography>
            )}
          </Stack>
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              startIcon={<Iconify icon="eva:sync-fill" />}
              onClick={async () => {
                try {
                  const r = await fetch('https://us-central1-bb-target-center.cloudfunctions.net/runMemberSyncNow', { method: 'POST' });
                  const data = await r.json();
                  if (r.ok) {
                    alert(`Sincronización completada: ${data.deactivated} desactivados, ${data.activated} reactivados.`);
                    window.location.reload();
                  } else {
                    alert(`Error: ${data.error || 'desconocido'}`);
                  }
                } catch (e) { alert('Error de red'); }
              }}
            >
              Sincronizar status
            </Button>
            <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={() => goToUser(null)}>
              New User
            </Button>
          </Stack>
        </Stack>

        {usersLoading ? <StatCardSkeleton count={4} /> : (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Members"
              count={stats.total}
              icon="eva:people-fill"
              color="#637381"
              active={!statusFilter}
              onClick={() => setStatusFilter(null)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Active Members"
              count={stats.active}
              icon="eva:checkmark-circle-2-fill"
              color="#22C55E"
              active={statusFilter === 'active'}
              onClick={() => handleStatClick('active')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Expired"
              count={stats.expired}
              icon="eva:close-circle-fill"
              color="#FF5630"
              active={statusFilter === 'expired'}
              onClick={() => handleStatClick('expired')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Expiring Soon"
              count={stats.expiringSoon}
              icon="eva:alert-triangle-fill"
              color="#FFAB00"
              active={statusFilter === 'expiring'}
              onClick={() => handleStatClick('expiring')}
            />
          </Grid>
          {stats.mismatched > 0 && (
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Active pero vencidos"
                count={stats.mismatched}
                icon="eva:flash-off-fill"
                color="#FFAB00"
                active={statusFilter === 'mismatched'}
                onClick={() => handleStatClick('mismatched')}
              />
            </Grid>
          )}
          {stats.inactiveMismatched > 0 && (
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Fecha vigente sin activar"
                count={stats.inactiveMismatched}
                icon="eva:alert-circle-fill"
                color="#2065D1"
                active={statusFilter === 'inactive_mismatch'}
                onClick={() => handleStatClick('inactive_mismatch')}
              />
            </Grid>
          )}
        </Grid>
        )}

        {filterLabel && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <Chip
              label={`Showing: ${filterLabel}`}
              onDelete={() => setStatusFilter(null)}
              color="primary"
              variant="outlined"
            />
          </Stack>
        )}

        {/* Filtro por rango de fechas (pedido de Afía 2026-08-24) */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
          <TextField
            select
            size="small"
            label="Filtrar por fecha de"
            value={dateField}
            onChange={(e) => { setDateField(e.target.value); setPage(0); }}
            SelectProps={{ native: true }}
            sx={{ minWidth: 200 }}
          >
            <option value="expiration">Expiración (vencimiento)</option>
            <option value="createdAt">Cuenta creada</option>
          </TextField>
          <TextField
            type="date"
            size="small"
            label="Desde"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="date"
            size="small"
            label="Hasta"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
            InputLabelProps={{ shrink: true }}
          />
          {(dateFrom || dateTo) && (
            <Chip
              label={`${dateField === 'expiration' ? 'Expiración' : 'Cuenta creada'}: ${dateFrom || '…'} → ${dateTo || '…'} (${dateFilteredUsers.length})`}
              onDelete={() => { setDateFrom(''); setDateTo(''); setPage(0); }}
              color="primary"
              variant="outlined"
            />
          )}
        </Stack>

        <Card>
          <UserListToolbar searchName="Search user..." numSelected={selected.length} filterName={filterName} onFilterName={handleFilterByName} downloadReport={downloadUsersCSV} />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={dateFilteredUsers.length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                />
                <TableBody>
                  {usersLoading ? <TableSkeleton rows={rowsPerPage} columns={8} hasAvatar hasCheckbox /> : null}
                  {!usersLoading && (showAll ? filteredUsers : filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)).map((r) => {
                    const { id, fullName, email, photo, memberId, active, phone, expiration, createdAt } = r;
                    const name = `${fullName}`;
                    const duplicateReason = memberDuplicateReason(r, duplicateKeys);
                    const profileImageUrl = photo?.[0]?.downloadURL ?? '';
                    const selectedUser = selected.indexOf(name) !== -1;

                    return (
                      <TableRow
                        hover
                        key={id}
                        tabIndex={-1}
                        role="checkbox"
                        selected={selectedUser}
                        onClick={() => goToUser(id)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                          <Checkbox checked={selectedUser} onChange={(event) => handleClick(event, name)} />
                        </TableCell>

                        <TableCell component="th" scope="row" padding="none">
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Avatar alt={name} src={profileImageUrl} sx={{ bgcolor: 'primary.main', width: 36, height: 36, fontSize: 14 }}>
                              {name?.charAt(0) || '?'}
                            </Avatar>
                            <Typography variant="subtitle2" noWrap>{name}</Typography>
                            {duplicateReason && (
                              <Tooltip title={`${duplicateReason}. Verifica cuál es la buena antes de cobrarle.`}>
                                <Chip
                                  label="Duplicado"
                                  size="small"
                                  sx={{ bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 600, height: 20 }}
                                />
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" noWrap>{email}</Typography>
                        </TableCell>

                        <TableCell>{formatPhoneDisplay(phone)}</TableCell>

                        <TableCell>{memberId}</TableCell>

                        <TableCell>
                          {(() => {
                            const status = getEffectiveStatus(r);
                            if (status === 'active') return <Chip label={STATUS_LABELS.active} color="success" size="small" />;
                            if (status === 'expired_mismatch') {
                              return (
                                <Chip
                                  label={STATUS_LABELS.expired_mismatch}
                                  color="warning"
                                  size="small"
                                  title="active=true pero la fecha de expiración ya pasó"
                                />
                              );
                            }
                            if (status === 'inactive_mismatch') {
                              return (
                                <Chip
                                  label={STATUS_LABELS.inactive_mismatch}
                                  color="info"
                                  size="small"
                                  title="La fecha de expiración está vigente pero el usuario no está marcado como Active"
                                />
                              );
                            }
                            return <Chip label={STATUS_LABELS.expired} color="error" size="small" />;
                          })()}
                        </TableCell>

                        <TableCell>{formatDate(expiration)}</TableCell>

                        <TableCell>{formatDate(createdAt)}</TableCell>

                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <IconButton size="small" onClick={(event) => handleOpenMenu(event, r)}>
                            <Iconify icon="eva:more-vertical-fill" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={9} />
                    </TableRow>
                  )}
                </TableBody>

                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={9} sx={{ py: 3 }}>
                        <Paper sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" paragraph>Not found</Typography>
                          <Typography variant="body2">
                            No results found for &nbsp;
                            <strong>&quot;{filterName}&quot;</strong>.
                            <br /> Try checking for typos or using complete words.
                          </Typography>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100, { label: 'All', value: -1 }]}
            component="div"
            count={filteredUsers.length}
            rowsPerPage={showAll ? filteredUsers.length || 1 : rowsPerPage}
            page={showAll ? 0 : page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelDisplayedRows={({ from, to, count }) =>
              showAll ? `${count} de ${count}` : `${from}–${to} de ${count}`
            }
          />
        </Card>
      </Container>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 140,
          },
        }}
      >
        <MenuItem onClick={handleEditClick}>
          <Iconify icon="eva:edit-fill" sx={{ mr: 2 }} />
          Edit
        </MenuItem>

        <MenuItem sx={{ color: 'error.main' }} onClick={handleDeleteClick(row)}>
          <Iconify icon="eva:trash-2-outline" sx={{ mr: 2 }} />
          Delete
        </MenuItem>
      </Popover>
    </>
  );
}
